import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import {
  TrailerViewModel,
  TrailerStatus,
} from "@app/shared/models/trailers/TrailerViewModel.model";
import { TractorStatus } from "@app/shared/models/tractors/TractorViewModel.model";
import { TrailerService } from "@app/trailer/shared/trailer.service";
import { DriverService } from "@app/driver/shared/driver.service";

@Component({
  selector: "app-trailer-profile",
  templateUrl: "./trailer-profile.component.html",
  styleUrls: ["./trailer-profile.component.css"],
})
export class TrailerProfileComponent implements OnInit, OnChanges {
  @Input() trailer: TrailerViewModel;
  @Input() isEditMode: boolean;
  @Input() terminals: Array<string>;
  @Input() states: { abbreviation: string; fullName: string }[];
  @Input() dateInputMask: any;

  @Output() hasChanges = new EventEmitter<boolean>();

  private originalTrailer: string;
  trailerStatuses = TractorStatus;
  trailerStatusOptions = [
    { value: TractorStatus.Available, label: "Available" },
    { value: TractorStatus.Shop, label: "Shop" },
    { value: TractorStatus.OOS, label: "OOS" },
  ];
  trailerTypeOptions = [
    { label: "Chasis", value: "CHASIS" },
    { label: "Trailer", value: "TRAILER" },
    { label: "Container", value: "CONTAINER" },
  ];
  mobileDeviceTypeOptions = [
    { label: "Phone", value: "PHONE" },
    { label: "Tablet", value: "TABLET" },
    { label: "DSN", value: "DSN" },
  ];
  equipmentCodes: any[] = [];
  equipmentCodeSelected: any = {};
  drivers: any[] = [];

  constructor(
    private trailerService: TrailerService,
    private driverService: DriverService
  ) {}

  ngOnInit() {
    this.originalTrailer = JSON.stringify(this.trailer);
    this.loadEquipmentCodes();
    this.loadDrivers();
  }

  loadEquipmentCodes() {
    this.trailerService.listTypes().subscribe(
      (equipmentCodes: any) => {
        this.equipmentCodes = equipmentCodes;
        this.setEquipmentCodeSelected();
      },
      (err) => {
        console.error("Error loading equipment codes:", err);
      }
    );
  }

  loadDrivers() {
    this.driverService.listSelectFormat(true, false).subscribe(
      (drivers: any) => {
        this.drivers = drivers;
        this.setDriverFromId();
      },
      (error) => {
        console.error("Error loading drivers:", error);
      }
    );
  }

  setDriverFromId() {
    // If trailer has a driverId but no driver object, find and set it
    if (this.trailer.driverId && this.drivers.length > 0) {
      const foundDriver = this.drivers.find((d) => d.id === this.trailer.driverId);
      if (
        foundDriver &&
        (!this.trailer.driver || this.trailer.driver.id !== this.trailer.driverId)
      ) {
        this.trailer.driver = foundDriver;
      }
    } else if (!this.trailer.driverId) {
      this.trailer.driver = null;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["trailer"] && !changes["trailer"].firstChange) {
      this.originalTrailer = JSON.stringify(this.trailer);
      this.setEquipmentCodeSelected();
      this.setDriverFromId();
      this.checkForChanges();
    }
  }

  setEquipmentCodeSelected() {
    if (this.trailer.equipmentCode && this.equipmentCodes.length > 0) {
      this.equipmentCodeSelected =
        this.equipmentCodes.find((equ: any) => equ.equipmentCode === this.trailer.equipmentCode) ||
        {};
    } else {
      this.equipmentCodeSelected = {};
    }
  }

  onFieldChange() {
    // Sync trailerNo and unitNumber
    if (this.trailer.trailerNo !== undefined) {
      this.trailer.unitNumber = this.trailer.trailerNo;
    }
    this.checkForChanges();
  }

  onEquipmentCodeChange() {
    if (this.equipmentCodeSelected && this.equipmentCodeSelected.equipmentCode) {
      this.trailer.equipmentCode = this.equipmentCodeSelected.equipmentCode;
    } else {
      this.trailer.equipmentCode = "";
    }
    this.onFieldChange();
  }

  onDriverChange() {
    // Update the driver object when driverId changes
    if (this.trailer.driverId) {
      const selectedDriver = this.drivers.find((d) => d.id === this.trailer.driverId);
      if (selectedDriver) {
        this.trailer.driver = selectedDriver;
      }
    } else {
      this.trailer.driver = null;
    }
    this.onFieldChange();
  }

  private checkForChanges() {
    const currentTrailer = JSON.stringify(this.trailer);
    const changed = currentTrailer !== this.originalTrailer;
    this.hasChanges.emit(changed);
  }

  getTrailerStatusLabel(): string {
    if (this.trailer?.status === undefined || this.trailer?.status === null) {
      return "Available";
    }
    const status = this.trailerStatusOptions.find((opt) => opt.value === this.trailer.status);
    return status ? status.label : "Available";
  }

  compareEquipmentCode(c1: any, c2: any): boolean {
    return c1 && c2 ? c1.equipmentCode === c2.equipmentCode : c1 === c2;
  }
}
